import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { whenFeedReady } from "@/lib/lowPriority";
import { PRAYER_ACCENT, applyPray, applyReact, excerpt, fill, prayerExcerptChars, prayingLabel, togglePray, toggleReact } from "@/lib/prayers";
import { PrayerHeader } from "@/components/prayers/PrayerCard";
import { PrayerReactions } from "@/components/prayers/PrayerReactions";
import { DismissX } from "@/components/smart/DismissX";

export const SPOTLIGHT_ROTATE_MS = 30000;

// `rotateMs` > 0 : une nouvelle prière (différente de celle affichée) toutes les 30 s tant que l'onglet est visible et
// qu'il reste au moins deux demandes à proposer — sinon le lecteur voyait toujours la même.
export const useSpotlight = (enabled, rotateMs = 0) => {
  const [data, setData] = useState(null);
  const ref = useRef(null);
  const load = useCallback((exclude = null) => api.get("/prayers/spotlight", exclude ? { params: { exclude } } : undefined)
    .then(({ data: d }) => { ref.current = d; setData(d); })
    .catch(() => setData({ enabled: false, items: [], waiting_count: 0 })), []);
  useEffect(() => {
    if (!enabled) return undefined;
    const cancel = whenFeedReady(load);
    const onChange = () => load();
    window.addEventListener("yalola:prayer_changed", onChange);
    return () => { cancel(); window.removeEventListener("yalola:prayer_changed", onChange); };
  }, [enabled, load]);
  useEffect(() => {
    if (!enabled || !rotateMs) return undefined;
    const tick = () => {
      const d = ref.current;
      if (document.visibilityState !== "visible" || !d || d.enabled === false || (Number(d.pool_size) || 0) < 2) return;
      load(d.items?.[0]?.id || null);
    };
    const id = setInterval(tick, rotateMs);
    return () => clearInterval(id);
  }, [enabled, rotateMs, load]);
  const patch = (id, fn) => setData((d) => (d ? { ...d, items: d.items.map((p) => (p.id === id ? fn(p) : p)) } : d));
  return { data, patch };
};

const usePrayActions = (patch, user, t) => {
  const pray = async (p) => {
    if (!user) return toast.error(t("prayers.login_to_pray", "Connecte-toi pour prier"));
    try { const r = await togglePray(p.id); patch(p.id, (x) => applyPray(x, r, user)); } catch { toast.error(t("common.error", "Erreur")); }
  };
  const react = async (p, type) => {
    if (!user) return;
    try { const r = await toggleReact(p.id, type); patch(p.id, (x) => applyReact(x, r)); } catch { toast.error(t("common.error", "Erreur")); }
  };
  return { pray, react };
};

const SpotlightRow = ({ p, user, onPray, onReact, testPrefix }) => {
  const { t, lang, config } = useApp();
  const nav = useNavigate();
  const [full, setFull] = useState(false); // lot 675 : « Lire la suite » sur place, sans quitter le fil
  const open = () => nav(`/prayers?focus=${p.id}`);
  const limit = prayerExcerptChars(config);
  const long = (p.content || "").trim().length > limit;
  return (
    <div className="rounded-xl p-3 space-y-2 cursor-pointer hover:bg-[color:var(--yalola-surface-alt)] transition-colors min-w-0" onClick={open} role="link" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") open(); }} data-testid={`${testPrefix}-item-${p.id}`}>
      <PrayerHeader p={p} lang={lang} t={t} compact />
      <p className="text-sm leading-snug whitespace-pre-wrap break-words" style={{ color: "var(--yalola-text)" }} data-testid={`${testPrefix}-text-${p.id}`} data-full={full ? "1" : "0"}>
        {full || !long ? p.content : excerpt(p.content, limit)}
        {long && (
          <button type="button" onClick={(e) => { e.stopPropagation(); setFull((v) => !v); }} className="ml-1 text-sm font-semibold hover:underline" style={{ color: PRAYER_ACCENT, background: "transparent" }} data-testid={`${testPrefix}-text-toggle-${p.id}`}>
            {full ? t("nav.see_less", "Voir moins") : t("prayers.read_more", "Lire la suite")}
          </button>
        )}
      </p>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <PrayerReactions p={p} user={user} onPray={onPray} onReact={onReact} compact />
        <span className="text-[11px] truncate" style={{ color: "var(--yalola-text-muted)" }}>{prayingLabel(p, user, t)}</span>
      </div>
    </div>
  );
};

const Title = ({ t, sub }) => (
  <div className="flex items-center gap-2">
    <span className="h-8 w-8 rounded-full grid place-items-center shrink-0" style={{ background: PRAYER_ACCENT + "1f" }}><HeartHandshake size={16} color={PRAYER_ACCENT} /></span>
    <div className="min-w-0">
      <div className="font-semibold text-sm" style={{ fontFamily: "Outfit" }}>{sub ? t("prayers.feed_title", "Prions ensemble") : t("prayers.spotlight_title", "Prières du moment")}</div>
      {sub && <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{t("prayers.feed_sub", "Des membres comptent sur ta prière")}</div>}
    </div>
  </div>
);

// variant: "sidebar" (PC right column) · "feed" (card inside the Home feed) · "compact" (one-line phone card).
// `data` : { data, patch } fourni par les cartes intelligentes (aucune requête interne alors).
export const PrayersSpotlightWidget = ({ variant = "sidebar", data: shared = null, onDismiss = null }) => {
  const { user, t, config } = useApp();
  const feats = config?.settings?.features || {};
  const enabled = feats.prayers !== false && (variant !== "feed" || feats.prayers_feed_card !== false);
  // Rotation 30 s (fondu) pour le widget de la colonne PC seulement — la carte du fil garde sa prière (clé de masquage stable).
  const own = useSpotlight(enabled && !shared, variant === "sidebar" ? SPOTLIGHT_ROTATE_MS : 0);
  const { data, patch } = shared || own;
  const { pray, react } = usePrayActions(patch, user, t);
  if (!enabled || !data || data.enabled === false) return null;
  const items = data.items || [];
  const waiting = Number(data.waiting_count) || 0;
  const testPrefix = `prayers-spotlight-${variant}`;

  if (variant === "compact") {
    if (!waiting) return null;
    return (
      <div className="yalola-card p-3 flex items-center gap-3 hover:bg-[color:var(--yalola-surface-alt)] transition-colors" data-testid={testPrefix}>
        <Link to="/prayers" className="flex items-center gap-3 flex-1 min-w-0">
          <span className="h-9 w-9 rounded-xl grid place-items-center shrink-0" style={{ background: PRAYER_ACCENT + "1f" }}><HeartHandshake size={18} color={PRAYER_ACCENT} /></span>
          <span className="flex-1 text-sm leading-snug" data-testid={`${testPrefix}-count`}>{fill(t("prayers.spotlight_waiting", "{n} prière(s) attendent ton intercession"), { n: waiting })}</span>
          <span className="text-xs font-semibold shrink-0 inline-flex items-center gap-0.5" style={{ color: PRAYER_ACCENT }}>{t("prayers.open", "Ouvrir")} <ChevronRight size={14} /></span>
        </Link>
        <DismissX onClick={onDismiss} testId={`${testPrefix}-dismiss`} label={t("smart_cards.dismiss", "Masquer")} />
      </div>
    );
  }

  if (variant === "feed" && !items.length) return null;
  return (
    <div className="yalola-card p-4 space-y-2" data-testid={testPrefix} data-count={items.length}>
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0"><Title t={t} sub={variant === "feed"} /></div>
        <DismissX onClick={onDismiss} testId={`${testPrefix}-dismiss`} label={t("smart_cards.dismiss", "Masquer")} />
      </div>
      {waiting > 0 && <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid={`${testPrefix}-count`}>{fill(t("prayers.spotlight_waiting", "{n} prière(s) attendent ton intercession"), { n: waiting })}</p>}
      {!items.length && <p className="text-sm py-2" style={{ color: "var(--yalola-text-muted)" }} data-testid={`${testPrefix}-empty`}>{t("prayers.spotlight_empty", "Aucune demande en attente. Partage la tienne 🙏")}</p>}
      {/* Une seule demande, tirée au sort pondéré côté serveur (urgentes ×2) parmi les récentes non encore portées ; la clé = id → fondu à chaque rotation.
          Lot 673 : `min-w-0` sur l'élément de grille — sans lui, un nom d'auteur long (nowrap) élargit la rangée au-delà du widget
          (grille `1fr` = minmax(auto, 1fr)) et le badge « Urgente » sort de la carte (capture owner, 2.3). */}
      <div className={variant === "feed" ? "grid grid-cols-[minmax(0,1fr)] gap-2" : "divide-y"} style={{ borderColor: "var(--yalola-border)" }}>
        {items.slice(0, 1).map((p) => (
          <div key={p.id} className={`animate-in fade-in duration-500 min-w-0 ${variant === "feed" ? "rounded-xl border overflow-hidden" : ""}`} style={variant === "feed" ? { borderColor: "var(--yalola-border)" } : undefined} data-testid={`${testPrefix}-current`} data-id={p.id}>
            <SpotlightRow p={p} user={user} onPray={pray} onReact={react} testPrefix={testPrefix} />
          </div>
        ))}
      </div>
      <Link to="/prayers" className="inline-flex items-center gap-1 text-xs font-semibold hover:underline" style={{ color: PRAYER_ACCENT }} data-testid={`${testPrefix}-all`}>
        {t("prayers.spotlight_all", "Voir le mur de prières")} <ChevronRight size={14} />
      </Link>
    </div>
  );
};
